import axios from '@/plugins/axios';

// Return null when this error is not recoverable here, false on cancellation.
export async function resolveMissingInvoicePeriod(vm, error, invoice = vm.invoice, invoiceDateOverride = null) {
    if (!error.details?.accounting_period || !invoice?.billingaccount ||
        !vm.userHasPermission('billing.change_billingaccount') ||
        !vm.userHasPermission('billing.view_billingaccount')) return null;
    const accountId = invoice.billingaccount;
    const entity = vm.current_entity_id;
    const invoiceDate = invoiceDateOverride || (vm.billingaccount?.handle_invoices
        ? invoice.invoice_date : (vm.invoice_date || invoice.invoice_date));
    if (!invoiceDate) return null;
    let offset = 0;
    while (true) {
        const { data } = await axios.get(`/api/billingaccounts/${accountId}/accounting-periods/`, { params: { limit: 100, offset } });
        if (vm.current_entity_id !== entity) return false;
        const periods = data.results || data;
        // A covering but closed period must retain the server's blocking error.
        if (periods.some(period => period.start_date <= invoiceDate && period.end_date >= invoiceDate)) return null;
        if (!data.next || !periods.length) break;
        offset += periods.length;
    }
    const { openAccountingPeriodDialog } = await import('@/utils/accounting-period-dialog');
    if (vm.current_entity_id !== entity) return false;
    const created = await openAccountingPeriodDialog(vm, {
            billingaccountId: accountId,
            billingaccountName: invoice.billingaccount_data?.name || vm.billingaccount?.name,
            invoiceDate,
            invoiceReference: invoice.invoice_id,
    });
    return !!created?.id && vm.current_entity_id === entity;
}

// Provider invoice serializers expose the purchase account as `billingaccount`.
export async function validateProviderInvoiceWithPeriod(vm, invoice, validate) {
    try {
        await validate();
        return true;
    } catch (error) {
        const details = error.details || error.response?.data;
        if (!details?.accounting_period) throw error;
        const entity = vm.current_entity_id;
        const { data: currentInvoice } = await axios.get(`/api/providerinvoices/${invoice.id}/`);
        if (vm.current_entity_id !== entity) return false;
        const resolved = await resolveMissingInvoicePeriod(vm, { details }, currentInvoice, currentInvoice.invoice_date);
        if (resolved === null) throw error;
        if (!resolved) return false;
        await validate(true);
        return true;
    }
}

export function recoverProviderInvoiceBulkPeriods(vm, result, payload = {}) {
    return recoverInvoiceBulkPeriods(vm, result, payload, 'providerinvoices');
}

export function recoverCustomerInvoiceBulkPeriods(vm, result, payload = {}) {
    return recoverInvoiceBulkPeriods(vm, result, payload, 'customerinvoices');
}

async function recoverInvoiceBulkPeriods(vm, result, payload, resource) {
    const entity = vm.current_entity_id;
    const retryIds = [];
    for (const [id, details] of Object.entries(result.error_details || {})) {
        if (!details.accounting_period) continue;
        if (vm.current_entity_id !== entity) return result;
        const { data: invoice } = await axios.get(`/api/${resource}/${id}/`);
        if (vm.current_entity_id !== entity) return result;
        let offset = 0;
        let covering;
        if (invoice.billingaccount && invoice.invoice_date) {
            while (true) {
                const { data } = await axios.get(`/api/billingaccounts/${invoice.billingaccount}/accounting-periods/`, { params: { limit: 100, offset } });
                if (vm.current_entity_id !== entity) return result;
                const periods = data.results || data;
                covering = periods.find(period => period.start_date <= invoice.invoice_date && period.end_date >= invoice.invoice_date);
                if (covering || !data.next || !periods.length) break;
                offset += periods.length;
            }
        }
        if (covering) {
            if (!covering.closed_at) retryIds.push(Number(id));
            continue;
        }
        const resolved = await resolveMissingInvoicePeriod(vm, { details }, invoice, invoice.invoice_date);
        if (resolved === false) break;
        if (resolved) retryIds.push(Number(id));
    }
    if (!retryIds.length || vm.current_entity_id !== entity) return result;
    const { data: retried } = await axios.post(`/api/${resource}/bulk/`, {
        action: 'validate', instances: retryIds, payload,
    });
    const retriedIds = new Set(retryIds.map(String));
    return {
        ...result,
        success: [...(result.success || []), ...(retried.success || [])],
        errors: [...(result.errors || []).filter(([id]) => !retriedIds.has(String(id))), ...(retried.errors || [])],
        error_details: {
            ...Object.fromEntries(Object.entries(result.error_details || {}).filter(([id]) => !retriedIds.has(id))),
            ...retried.error_details,
        },
    };
}
